import React, { useState, useContext, useEffect } from 'react';

import { TransactionFilterContext } from 'src/context/transaction-filter-context';

import { CactusImage } from 'src/assets/images';
import { FallenIceCream } from 'src/assets/images';
import { Card } from 'src/components';

import { DownloadCSVContext } from 'src/context/download-csv-context';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';
import { transformTransactionsForCsv } from 'src/helpers/csv-string-builder';
import { getSettlements, getTransactions } from 'src/services/transaction.service';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import TransactionRow from './transaction-row/transaction-row';
import { queue } from 'src/helpers/delay';

import './transaction-list.scss';

function acceptedTransaction(transaction, transactionType, reference, fromAmount, toAmount) {
  const transactionAmountNumber = Number(transaction.transactionAmount);

  const tType = transactionType ? transaction.transactionTypeDescription === transactionType : true;
  const tRef = reference ? transaction.retrievalReferenceNumber.includes(reference) : true;
  const tBounds =
    fromAmount && toAmount
      ? transactionAmountNumber >= fromAmount && transactionAmountNumber <= toAmount
      : true;

  return tType && tRef && tBounds;
}

function TransactionList(props) {
  const handleTechnicalError = useTechnicalErrorHandler();
  const { transactionFilter } = useContext(TransactionFilterContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [, setLastPageLoaded] = useState(false);

  const [settlements, setSettlements] = useState([]);
  const [settlementsTransactions, setSettlementsTransactions] = useState([]);
  const [filteredSettlementsTransactions, setFilteredSettlementsTransactions] = useState([]);

  const { setCsvDataGenerator } = useContext(DownloadCSVContext);
  const { merchant } = useContext(MerchantProfileContext);

  const noTransactionsFound =
    !isLoading && filteredSettlementsTransactions && Array.isArray(filteredSettlementsTransactions)
      ? filteredSettlementsTransactions.length === 0 ||
        filteredSettlementsTransactions.every((t) => t === null || t.length === 0)
      : true;

  // TODO: Improve/Rewrite transaction functionality, with changes to TradeRoot API,
  // changes need to be made to how transactions are displayed and how the search works
  const fetchTransactions = async (
    transactionFetchAbortController,
    _pageNumber,
    handleTechnicalError,
  ) => {
    setPageNumber(_pageNumber);
    setIsLoading(true);
    setSettlements([]);
    setSettlementsTransactions([]);

    const settlementResponse = await getSettlements(
      transactionFilter.selectedDates[0],
      transactionFilter.selectedDates[1],
      transactionFetchAbortController,
      _pageNumber,
      handleTechnicalError,
      false,
      true,
    );

    let settlementsTransactionsResponses = [];
    let settlementsTransactionsResponse;

    for (let i = 0; i < settlementResponse?.Rows.length; ++i) {
      settlementsTransactionsResponse = await getTransactions(
        settlementResponse.Rows[i].transactionUUID || null,
        transactionFilter.selectedDates[0],
        transactionFilter.selectedDates[1],
        transactionFetchAbortController,
        pageNumber,
        transactionFilter.transactionSearch.reference !== ''
          ? transactionFilter.transactionSearch.reference
          : null,
        transactionFilter.transactionSearch.fromAmount || null,
        transactionFilter.transactionSearch.toAmount || null,
        transactionFilter.dropdownFilters.transactionType !== 'Transaction Type'
          ? transactionFilter.dropdownFilters.transactionType
          : null,
        handleTechnicalError,
        false,
        true,
      );

      if (!transactionFetchAbortController.signal.aborted) {
        if (settlementsTransactionsResponse?.Rows) {
          settlementsTransactionsResponses.push(settlementsTransactionsResponse.Rows);
        } else {
          settlementsTransactionsResponses.push(null);
        }
      } else break;
    }

    setSettlementsTransactions(settlementsTransactionsResponses);

    filterSettlementsTransactions(settlementsTransactionsResponses);

    if (settlementResponse?.Rows) {
      setSettlements(settlementResponse.Rows);
    }

    if (settlementResponse?.NumberOfPages) {
      setLastPageLoaded(_pageNumber === settlementResponse.NumberOfPages);
    }

    if (!transactionFetchAbortController.signal.aborted) {
      setIsLoading(false);
    }
  };

  const setExport = async (transactionFetchAbortController, handleTechnicalError) => {
    setCsvDataGenerator(() => {
      // return function to be called only when download/export is clicked
      return async () => {
        let selectedTransactionType = transactionFilter.dropdownFilters.transactionType;

        if (selectedTransactionType === 'Transaction Type') selectedTransactionType = undefined;

        const transactions = await getTransactions(
          null,
          transactionFilter.selectedDates[0],
          transactionFilter.selectedDates[1],
          transactionFetchAbortController,
          1,
          transactionFilter.transactionSearch.reference || null,
          transactionFilter.transactionSearch.fromAmount || null,
          transactionFilter.transactionSearch.toAmount || null,
          selectedTransactionType || null,
          handleTechnicalError,
        );

        const completedTransactionList = [];
        completedTransactionList.push(...transactions.Rows);

        if (transactions.NumberOfPages > 1) {
          for (let index = 2; index <= transactions.NumberOfPages; index++) {
            const transactionListResult = await getTransactions(
              null,
              transactionFilter.selectedDates[0],
              transactionFilter.selectedDates[1],
              transactionFetchAbortController,
              index,
              transactionFilter.transactionSearch.reference || null,
              transactionFilter.transactionSearch.fromAmount || null,
              transactionFilter.transactionSearch.toAmount || null,
              selectedTransactionType || null,
              handleTechnicalError,
            );
            completedTransactionList.push(...transactionListResult.Rows);
          }
        }

        return transformTransactionsForCsv(
          merchant.companyName,
          transactionFilter.selectedDates,
          completedTransactionList,
        );
      };
    });
  };

  useEffect(() => {
    const transactionFetchAbortController = new AbortController();
    if (
      transactionFilter &&
      (Object.keys(transactionFilter.transactionSearch).length > 0 ||
        (transactionFilter.dropdownFilters.transactionType &&
          transactionFilter.dropdownFilters.transactionType !== 'Transaction Type'))
    ) {
      setIsSearching(true);

      filterSettlementsTransactions(settlementsTransactions);
    } else {
      setIsSearching(false);
      queue.push(() => fetchTransactions(transactionFetchAbortController, 1));
    }

    setExport(transactionFetchAbortController, handleTechnicalError);

    return () => {
      // this abort controller will be used to cancel the fetch request in the scenario where the component is unloaded while the request is still in flight
      transactionFetchAbortController.abort();
    };
  }, [transactionFilter, setCsvDataGenerator, merchant]);

  useEffect(() => {
    // Only change the transaction types on first load, not each time a new transaction type filter is applied
    if (!isLoading && settlementsTransactions.length > 0 && !isSearching) {
      const filtered = getFilterItems();
      props.createFilterItems(filtered);
    }
  }, [isLoading]);

  useEffect(() => {
    props.createFilterItems({
      transactionTypes: ['Transaction Type'],
    });
  }, []);

  const getFilterItems = () => {
    const transactionTypes = [];

    transactionTypes.push('Transaction Type');

    settlementsTransactions.forEach((transactionGroup) => {
      if (transactionGroup) {
        transactionGroup.forEach((transaction) => {
          transactionTypes.push(transaction.transactionTypeDescription);
        });
      }
    });

    return {
      transactionTypes: [...new Set(transactionTypes)],
    };
  };

  const filterSettlementsTransactions = (sTrans) => {
    const fTrans = [];

    sTrans.forEach((transaction) => {
      fTrans.push(filterTransactions(transaction));
    });

    setFilteredSettlementsTransactions(fTrans);
  };

  const filterTransactions = (transactions) => {
    const trType =
      transactionFilter.dropdownFilters.transactionType !== 'Transaction Type'
        ? transactionFilter.dropdownFilters.transactionType
        : undefined;
    const reference =
      transactionFilter.transactionSearch.reference !== ''
        ? transactionFilter.transactionSearch.reference
        : undefined;
    const fromAmount = transactionFilter.transactionSearch.fromAmount || undefined;
    const toAmount = transactionFilter.transactionSearch.toAmount || undefined;

    let fTrans = null;

    if (transactions && transactions.length > 0) {
      fTrans = transactions.filter((transaction) =>
        acceptedTransaction(transaction, trType, reference, fromAmount, toAmount),
      );
    }

    return fTrans;
  };

  const areSearchResults =
    transactionFilter.transactionSearch.reference ||
    (transactionFilter.transactionSearch.fromAmount &&
      transactionFilter.transactionSearch.toAmount);

  return (
    <div
      className={'TransactionList transaction-table-container '}
      data-testid="transaction-table-container"
    >
      {isSearching && areSearchResults && (
        <>
          <span className="search-results-title is-hidden-mobile">
            Showing {settlements.length} of {settlements.length} results for &nbsp;
            <b>
              {transactionFilter.transactionSearch.reference
                ? `Reference no. ${transactionFilter.transactionSearch.reference}`
                : `Amounts: R${formatCurrencyAmountSimple(
                    transactionFilter.transactionSearch.fromAmount,
                  )} - R${formatCurrencyAmountSimple(
                    transactionFilter.transactionSearch.toAmount,
                  )}`}
            </b>
          </span>
          <br />
        </>
      )}
      <Card stretchVertical={true} className="card-table">
        {settlements && !isLoading && !noTransactionsFound && (
          <table className="table is-fullwidth">
            <tbody>
              {settlements.map((settlement, tdIndex) => {
                return settlementsTransactions[tdIndex] &&
                  filteredSettlementsTransactions[tdIndex]?.length > 0 ? (
                  <TransactionRow
                    key={tdIndex}
                    settlementTransaction={settlement}
                    transactions={filteredSettlementsTransactions[tdIndex]}
                  />
                ) : (
                  ''
                );
              })}
            </tbody>
          </table>
        )}

        {!isLoading && !areSearchResults && noTransactionsFound && (
          <table className="table is-fullwidth no-data-table">
            <tbody>
              <tr>
                <td colSpan="6" className="has-text-centered no-data-image-container">
                  <img src={CactusImage} className="no-data-image" />
                </td>
              </tr>
              <tr>
                <td colSpan="6" className="has-text-centered no-data-text-container">
                  <span className="title">No data to display</span>
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {!isLoading && areSearchResults && noTransactionsFound && (
          <table className="table is-fullwidth no-data-table">
            <tbody>
              <tr>
                <td colSpan="6" className="has-text-centered no-data-image-container">
                  <img src={FallenIceCream} className="no-data-image" />
                </td>
              </tr>
              <tr>
                <td colSpan="6" className="has-text-centered no-data-text-container">
                  <span className="title">
                    Oh no, we couldn’t find any results for your search!
                  </span>
                  <br />
                  <span className="subtitle">Try adjusting your search criteria</span>
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {isLoading && (
          <table className="table is-fullwidth">
            <tbody>
              <tr>
                <td colSpan="6" className="has-text-centered">
                  Loading...
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default TransactionList;
